import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs'; 
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { CaretDownOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Tabs, Dropdown, Layout, message, Space, Tag, Table } from 'antd';
import { routeHistory } from '@nackle/biw-web-router';
import Spinner from '../../components/Spinner/Spinner';
import AIReportsList from '../../components/AIReportsList/AIReportsList';
import ReportsList from '../Reporting/StaticReportingPage';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import useHasError from '../../hooks/useHasError'; 
import useIsLoading from '../../hooks/useIsLoading';
import { Routes } from '../../../routes'; 
import { useURLQueries } from '../../hooks/useURLQueries'; 
import DeleteButton from '../../components/DeleteButton/DeleteButton'; 
import useAIListPrompts from '../../gql/hooks/useAIListPrompts';
import useAIUpdatePrompt from '../../gql/hooks/useAIUpdatePrompt';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags';
import './styles.less';
import useAIListPromptReports from '../../gql/hooks/useAIListPromptReports';


// Applied to every column except the greedy name column: keeps headers and
// cell contents on a single line so they aren't compressed into wrapping.
const noWrap = {
    onHeaderCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
    onCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
};

const AIReportingListPage = ( props ) => {
    const { query: aiQuery, prompts, promptsLoading, promptsError, pageData } = useAIListPrompts( false );
    const { updatePrompt, isSaving } = useAIUpdatePrompt();
    const { hasError } = useHasError( { items: [ promptsError ] } );
    const { isLoading } = useIsLoading( { items: [ promptsLoading, isSaving ] } );
    const [ promptsList, setPromptsList ] = useState( [] );
    const [ expandedRows, setExpandedRows ] = useState( [] );
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDir, setSortDir] = useState('asc')
    const [ showAIReporting ] = useFeatureFlags( [ flags.AI_REPORTING_ENABLED ] );
    const [ showWarehouseReporting ] = useFeatureFlags( [ flags.STATIC_REPORTING_ENABLED ] );
    const [currentPage, setCurrentPage] = useState(1);
    
    const breadcrumbs = [
            { name: 'Course Studio', path: Routes.Home.buildPath() },
            { name: 'Analytics', path: Routes.Reporting.buildPath() },
            { name: 'AI Reporting'}
        ];

    function getUpdatedVars(newVars = null) {
        return {
            page: newVars?.page ?? currentPage,
            orderDir: newVars?.orderDir ?? sortDir,
            orderBy: newVars?.orderBy ?? sortBy,
            limit: 10,
        }
    }

    function search(newVars) {

        const updatedVars = getUpdatedVars(newVars)

        aiQuery({
            variables: updatedVars,
            fetchPolicy: 'network-only'
        })
    }

    useEffect( () => {
        search()
    }, [ ] );

    const handleDeleteClick = async ( item ) => {
        const vars = {
            promptId: item.id,
            label: item.label,
            isSaved: false,
        };

        const response = await updatePrompt( vars );
        const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
        
        if ( success ) {
            setPromptsList( promptsList.filter( i => i.id !== item.id ) );
        } else {
            message.error( 'Error deleting prompt.' );    
        }
    };
    
    const handleEditClick = ( item ) => {
        routeHistory.push( Routes.AIReportDetailsPage.buildPath( item.id ) );
    };
    
    const handleCreateClick = ( item ) => {
        routeHistory.push( Routes.AIReportCreatePage.buildPath() );
    };

    const handlePaginatorChange = ( pagination ) => {
        setCurrentPage( pagination );
    };

    const handleTableChange = ( pagination, filters, sorter ) => {      
        const order = sorter.order == 'ascend' ? 'asc' : 'desc';
        setCurrentPage( pagination.current );
        setSortDir(order);
        setSortBy( sorter.column?.dataIndex );

        search({page: pagination.current, orderDir: order, orderBy: sorter.column?.dataIndex})
    };

    const handleSetExpandedRows = ( rows ) => {
        setExpandedRows( rows );
    };

    const handleExpandCollapseClick = ( item ) => {
        if ( item.key === 'expand' ) {
            handleSetExpandedRows( promptsList?.map( i => i.id ) || [] ); 
        } else {
            handleSetExpandedRows( [] );
        }
    };

    const columns =  [ 
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Prompt Name',
            width: '100%',
            render: ( name ) => name
        },
        {
            key: 'isValid',
            dataIndex: 'isValid',
            title: 'Valid',  
            render: ( isValid ) => isValid === null ? 'N/A' :
                 ( isValid ? <Tag color="green">Valid</Tag> : <Tag color="red">Invalid</Tag> )
        },
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: 'Created At',
        },
        {
            key: 'actions', 
            title: 'Actions',
            fixed: 'right', 
            width: '100px',
            render: ( item ) => 
                <>
                    <Space>
                        <Button 
                            type="link" 
                            icon={ <EyeOutlined /> } 
                            onClick= { () => handleEditClick( item ) }
                        />
                                
                        <DeleteButton
                            id={ item.id }   
                            title={ 'Confirm Delete' }
                            deleteCallback={ ( i ) => handleDeleteClick( item ) }
                            confirmMessage= { 
                                <>
                                    <p>Are you <i>sure</i> you want to permanently delete the query <b>{ item.name }</b>? 
                                    This action cannot be undone.</p>
                                </>
                            }
                        />
                    </Space>
                </> 
        } 
    ];

    /*************************************
     *  ANCHOR Render
     *************************************/
    
    const renderExpandedRow = ( item ) => {
        return (
            <>
                { item.prompt && 
                    <div style={ { marginLeft: '50px' } }>
                        <strong>PROMPT:</strong>
                        <pre className="prompt-block">
                            { item.prompt }
                        </pre>
                    </div>
                
                }
                { item.sql && 
                    <div style={ { marginLeft: '50px' } }>
                        <strong>SQL:</strong>
                        <pre className="sql-block">
                            { item.sql }
                        </pre>
                    </div>
                
                }
                <AIReportsList 
                    promptId={ item.id } 
                    header={ <strong>AI Reports</strong> }
                />
            </>
        );
    };

    return ( 
        <Layout className="ai-reports-list-page">
            
            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
             
            { /* Main Content */ }
            { !isLoading &&

                <Card
                    className="ai-reporting-content"
                    title="AI Reporting"
                >   
                    <div className="ai-tab">
                        <div className="toolbar">
                            <Button
                                icon={ <PlusOutlined/> }
                                onClick={ () => handleCreateClick() }
                                type="text"
                            >
                                Create Prompt
                            </Button>
                        </div>
                        { /* Error message */ }
                        { hasError && 
                            <Alert 
                                type="error"
                                message="Error"
                                description="An unknown error occurred. Please try again later."
                            />
                        }

                        { !hasError && 
                            <Table
                                tableLayout="auto"
                                rowKey='id'
                                columns={ columns.map( c => c.key === 'name' ? c : { ...noWrap, ...c } ) }
                                dataSource={ prompts }
                                onChange={ handleTableChange }
                                pagination={ {
                                    onChange: handlePaginatorChange,
                                    total: pageData?.totalPages,
                                    defaultCurrent: currentPage,
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                                expandable={ {
                                    expandedRowKeys: expandedRows,
                                    onExpandedRowsChange: handleSetExpandedRows,
                                    expandedRowRender: ( item ) => renderExpandedRow( item ),
                                    columnTitle: (
                                        <Dropdown
                                            menu={ {
                                                items: [
                                                    {
                                                        label: 'Expand All',
                                                        key: 'expand'
                                                    },
                                                    {
                                                        label: 'Collapse All',
                                                        key: 'collapse',
                                                    } 
                                                ],
                                                onClick: handleExpandCollapseClick
                                            } }
                                        >
                                            <Button size="small">
                                                <CaretDownOutlined style={ { color: '#808080' } }/>
                                            </Button>
                                        </Dropdown>
                                    )
                                } }
                            />
                        }
                    </div>
                </Card>
            }
        </Layout>
    );
};

export default AIReportingListPage;
